import React, { useState, useEffect, useContext } from 'react';
import { 
  TextField, Switch, Button, Typography, Box, Grid, Card, CardContent, 
  Snackbar, Alert, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction 
} from '@mui/material';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import AppContext from '../AppContext';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const SettingsComponent = () => {
  const [salesTax, setSalesTax] = useState('');
  const [useShiftSystem, setUseShiftSystem] = useState(false);
  const [cashiers, setCashiers] = useState([]);
  const [newCashier, setNewCashier] = useState('');
  const [editingCashier, setEditingCashier] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const { shopId, t } = useContext(AppContext);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'shops', shopId));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setSalesTax(data.salesTax || '');
          setUseShiftSystem(data.useShiftSystem || false);
          setCashiers(data.cashiers || []);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        showSnackbar(t('Failed to load settings'), 'error');
      }
    };
    fetchSettings();
  }, [shopId]);

  const handleSaveSettings = async () => {
    try {
      await setDoc(doc(db, 'shops', shopId), {
        saleTax: salesTax,
        useShiftSystem: useShiftSystem,
        cashiers: cashiers
      }, { merge: true });
      showSnackbar(t('Settings saved successfully!'), 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showSnackbar(t('Failed to save settings. Please try again.'), 'error');
    }
  };

  const handleAddCashier = () => {
    if (newCashier.trim() && !cashiers.includes(newCashier.trim())) {
      setCashiers([...cashiers, newCashier.trim()]);
      setNewCashier('');
    }
  };

  const handleEditCashier = (index) => {
    setEditingCashier({ index, value: cashiers[index] });
  };

  const handleUpdateCashier = () => {
    if (editingCashier && editingCashier.value.trim()) {
      const updatedCashiers = [...cashiers];
      updatedCashiers[editingCashier.index] = editingCashier.value.trim();
      setCashiers(updatedCashiers);
      setEditingCashier(null);
    }
  };

  const handleDeleteCashier = (index) => {
    setCashiers(cashiers.filter((_, i) => i !== index));
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("General Settings")}
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Card variant='outlined'>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("Sales Tax Rate")}
              </Typography>
              <TextField
                label={t("Rate (%)")}
                type="number"
                value={salesTax}
                onChange={(e) => setSalesTax(e.target.value)}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card variant='outlined'>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("Shift System")}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Switch
                  checked={useShiftSystem}
                  onChange={(e) => setUseShiftSystem(e.target.checked)}
                />
                <Typography>{t("Use Shift System")}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card variant='outlined'>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("Cashiers")}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <TextField
                  label={t("Cashier Name")}
                  value={editingCashier ? editingCashier.value : newCashier}
                  onChange={(e) => editingCashier 
                    ? setEditingCashier({ ...editingCashier, value: e.target.value })
                    : setNewCashier(e.target.value)
                  }
                  sx={{ mr: 1 }}
                />
                {editingCashier ? (
                  <Button 
                    variant="contained" 
                    onClick={handleUpdateCashier}
                    sx={{ mr: 1 }}
                  >
                    {t("Update")}
                  </Button>
                ) : (
                  <Button 
                    variant="contained" 
                    onClick={handleAddCashier}
                    sx={{ mr: 1 }}
                  >
                    {t("Add")}
                  </Button>
                )}
                {editingCashier && (
                  <Button 
                    variant="outlined" 
                    onClick={() => setEditingCashier(null)}
                  >
                    {t("Cancel")}
                  </Button>
                )}
              </Box>
              <List>
                {cashiers.map((cashier, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={cashier} />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        onClick={() => handleEditCashier(index)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        onClick={() => handleDeleteCashier(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Box sx={{ mt: 3 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSaveSettings} 
        >
          {t("Save All Settings")}
        </Button>
      </Box>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsComponent;
